import React, { useState, useEffect, useCallback } from 'react';
import useAxiosPrivate from '../hooks/useAxiosPrivate';

const AffiliateDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [userSignedIn, setUserSignedIn] = useState(false);
  const [userData, setUserData] = useState(null);

  // Coupon creation states (admins only)
  const [coupon, setCoupon] = useState('');
  const [email, setEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);

  // Admin coupon list states
  const [adminCoupons, setAdminCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponsError, setCouponsError] = useState(null);

  // Promoter coupon-user list states
  const [couponData, setCouponData] = useState(null);

  const [loadingCouponUsers, setLoadingCouponUsers] = useState(false);
  const [couponUsersError, setCouponUsersError] = useState(null);

  const axiosPrivate = useAxiosPrivate();

  // --- fetch logged-in user ---
  const getUser = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosPrivate.get('/web/getuser', {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.status === 200) {
        setUserData(response.data);
        setUserSignedIn(true);
      } else {
        setUserSignedIn(false);
      }
    } catch (err) {
      if (err.name !== 'AbortError') setUserSignedIn(false);
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate]);

  // --- fetch admin’s coupons (level 2) ---
  const fetchAdminCoupons = useCallback(async () => {
    if (userData?.level !== 2) return;
    try {
      setLoadingCoupons(true);
      setCouponsError(null);
      const resp = await axiosPrivate.get('/admin/getAdminCoupons', {
        headers: { 'Content-Type': 'application/json' },
      });
      setAdminCoupons(resp.data.coupons || []);
    } catch {
      setCouponsError('Failed to load coupons.');
    } finally {
      setLoadingCoupons(false);
    }
  }, [axiosPrivate, userData]);

  // --- fetch promoter’s coupon users (level 1) ---
  const fetchCouponUsers = useCallback(async () => {
    if (userData?.level !== 1) return;

    try {
      setLoadingCouponUsers(true);
      setCouponUsersError(null);

      const resp = await axiosPrivate.post(
        '/admin/getCouponUsers',
        { coupon: userData.affiliate_coupon },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      setCouponData(resp.data || null);

    } catch (err) {
      if (err.response?.status === 404) {
        setCouponData(null);
        setCouponUsersError(null);
      } else {
        setCouponUsersError('Failed to load coupon users.');
      }
    } finally {
      setLoadingCouponUsers(false);
    }
  }, [axiosPrivate, userData]);


  useEffect(() => {
    getUser();
  }, [getUser]);

  useEffect(() => {
    fetchAdminCoupons();
    fetchCouponUsers();
  }, [fetchAdminCoupons, fetchCouponUsers]);

  const getLevelName = (level) => {
    switch (level) {
      case 1: return 'Promoter';
      case 2: return 'Admin';
      default: return 'Unknown';
    }
  };

  // --- handle creating a new coupon (admin only) ---
  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateResult(null);
    try {
      const response = await axiosPrivate.post(
        '/admin/create_coupon',
        { adminEmail: userData.email, coupon, userEmail: email },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.status === 200) {
        setCreateResult({ success: true, message: 'Coupon created successfully!' });
        fetchAdminCoupons();
      } else {
        setCreateResult({ success: false, message: 'Failed to create coupon.' });
      }
    } catch (err) {
      setCreateResult({
        success: false,
        message: err.response?.data?.message || 'Error occurred while creating coupon.'
      });
    } finally {
      setCreating(false);
      setCoupon('');
      setEmail('');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <span className="text-xl font-semibold">Loading...</span>
      </div>
    );
  }

  if (!userSignedIn || !userData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <span className="text-xl text-red-600">Not logged in or unable to fetch user.</span>
      </div>
    );
  }

  // level 0: not in affiliate program
  if (userData.level === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-black-50">
        <div className="bg-slate-800 shadow-lg rounded-lg p-8 w-full max-w-md mt-10 text-center">
          <h2 className="text-2xl font-bold mb-6">Affiliate Dashboard</h2>
          <div className="mb-4 text-lg text-red-400 font-semibold">
            You are not part of the affiliate program.<br />
            Please contact the owner.
          </div>
        </div>
      </div>
    );
  }

  // level 1 & 2 share the same outer shell
  return (
    <div className="flex flex-col items-center justify-start min-h-screen bg-black-50 px-4 py-8">
      <div className="w-full max-w-6xl space-y-8">

        <div className="bg-slate-800 rounded-xl shadow p-6 flex flex-col md:flex-row gap-6">
          {/* Left Panel: User Info & Stats */}
          <div className="w-full md:w-1/3 space-y-6 bg-slate-700 rounded-lg p-5">
            <div>
              <h3 className="text-xl font-semibold mb-3">Profile Info</h3>
              <div className="text-sm space-y-2">
                <p><span className="font-semibold">Name:</span> {userData.name}</p>
                <p><span className="font-semibold">Email:</span> {userData.email}</p>
                <p><span className="font-semibold">Role:</span> {getLevelName(userData.level)}</p>
              </div>
            </div>

            {userData.level === 2 && (
              <div>
                <h3 className="text-xl font-semibold mb-3">Create Coupon</h3>
                <form onSubmit={handleCreateCoupon} className="space-y-3">
                  <input
                    type="text"
                    className="w-full px-4 py-2 rounded bg-slate-600 text-white focus:outline-none"
                    placeholder="Coupon code"
                    value={coupon}
                    onChange={e => setCoupon(e.target.value)}
                    required
                  />
                  <input
                    type="email"
                    className="w-full px-4 py-2 rounded bg-slate-600 text-white focus:outline-none"
                    placeholder="Recipient email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                  <button
                    type="submit"
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded"
                    disabled={creating || !coupon || !email}
                  >
                    {creating ? 'Creating...' : 'Create'}
                  </button>
                  {createResult && (
                    <p className={`text-center text-sm font-medium ${createResult.success ? 'text-green-400' : 'text-red-400'}`}>
                      {createResult.message}
                    </p>
                  )}
                </form>
              </div>
            )}

            {userData.level === 1 && couponData && (
              <div className="border-t border-slate-600 pt-4">
                <h3 className="text-xl font-semibold mb-3">Earnings Summary</h3>
                <div className="text-sm space-y-2">
                  <p><span className="font-semibold">Total Earnings:</span> ₹{couponData.activePremiumUsers * couponData.commission_per_use}</p>
                  <p><span className="font-semibold">Active Premium Users:</span> {couponData.activePremiumUsers}</p>
                  {couponData.monthlySummary && (() => {
                    const months = Object.keys(couponData.monthlySummary);
                    const latestMonth = months.sort().reverse()[0];
                    const latest = couponData.monthlySummary[latestMonth];
                    return (
                      <p><span className="font-semibold">Last Paid:</span> ₹{latest.amount_paid} on {latest.paid_on}</p>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Right Panel */}
          <div className="w-full md:w-2/3 bg-slate-700 rounded-lg p-5">
            {userData.level === 2 && !loadingCoupons && !couponsError && adminCoupons.length > 0 ? (
              <>
                <h3 className="text-xl font-bold mb-4 text-center">Your Coupons</h3>
                <div className="overflow-x-auto">
                  <table className="w-full table-auto text-sm">
                    <thead>
                      <tr className="border-b border-slate-600 text-left">
                        <th className="px-3 py-2">Code</th>
                        <th className="px-3 py-2">Email</th>
                        <th className="px-3 py-2">Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminCoupons.map(c => (
                        <tr key={c.coupon_code} className="hover:bg-slate-600">
                          <td className="px-3 py-2">{c.coupon_code}</td>
                          <td className="px-3 py-2">{c.email}</td>
                          <td className="px-3 py-2">{c.usage_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : userData.level === 1 ? (
              <>
                <h3 className="text-xl font-bold mb-4 text-left">Users of Coupon <span className="text-blue-300">{userData.affiliate_coupon}</span></h3>
                {loadingCouponUsers ? (
                  <p className="text-center">Loading users…</p>
                ) : couponUsersError ? (
                  <p className="text-center text-red-400">{couponUsersError}</p>
                ) : !couponData?.users?.length ? (
                  <p className="text-center">No users found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto text-sm">
                      <thead>
                        <tr className="border-b border-slate-600 text-left">
                          <th className="px-3 py-2">Email</th>
                          <th className="px-3 py-2">Purchase Date</th>
                          <th className="px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {couponData.users.map((u, i) => (
                          <tr key={i} className="hover:bg-slate-600">
                            <td className="px-3 py-2 max-w-[180px] truncate">{u.email}</td>
                            <td className="px-3 py-2">{u.purchase_date}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 text-sm rounded font-semibold 
                              ${u.isPremium ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
                                {u.isPremium ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : (
              <p className="text-red-400 text-center">Nothing to display</p>
            )}
          </div>
        </div>
      </div>

      {userData.level === 1 && couponData?.monthlySummary && (
        <div className="w-full max-w-6xl mt-8 bg-slate-800 rounded-xl shadow p-6">
          <h3 className="text-2xl font-bold mb-4 text-center">Payment History Summary</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-auto">
              <thead>
                <tr className="border-b border-slate-600 text-left">
                  <th className="px-3 py-2">Month</th>
                  <th className="px-3 py-2">Active Users</th>
                  <th className="px-3 py-2">Inactive Users</th>
                  <th className="px-3 py-2">Payout</th>
                  <th className="px-3 py-2">Amount Paid</th>
                  <th className="px-3 py-2">Paid On</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(couponData.monthlySummary)
                  .sort(([a], [b]) => b.localeCompare(a)) // sort descending by month
                  .map(([month, data]) => (
                    <tr key={month} className="hover:bg-slate-700">
                      <td className="px-3 py-2 font-medium">{month}</td>
                      <td className="px-3 py-2">{data.active_users}</td>
                      <td className="px-3 py-2">{data.inactive_users}</td>
                      <td className="px-3 py-2">₹{data.payout}</td>
                      <td className="px-3 py-2">₹{data.amount_paid}</td>
                      <td className="px-3 py-2">{data.paid_on}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 text-sm rounded font-semibold
                    ${data.status === 'paid'
                            ? 'bg-green-600 text-white'
                            : data.status === 'partial'
                              ? 'bg-yellow-500 text-white'
                              : 'bg-red-600 text-white'}`}>
                          {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>

  );

};

export default AffiliateDashboard;
